/**
 * CookieBanner — GDPR Art. 7 consenso cookie.
 * Appare al primo accesso; la scelta viene salvata in localStorage.
 * Nessun cookie di terze parti viene caricato finché l'utente non accetta.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

const STORAGE_KEY = "aurea_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted: true, ts: Date.now() }));
    setVisible(false);
  };

  const reject = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ accepted: false, ts: Date.now() }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Informativa cookie"
      className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-border bg-background/95 backdrop-blur-sm shadow-xl"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-1 text-sm text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Utilizziamo i cookie</span> per garantire il corretto funzionamento della piattaforma e, previo consenso, per analisi delle performance (Sentry).
          {" "}
          <Link to="/privacy" className="underline underline-offset-2 text-foreground hover:text-primary transition-colors">
            Privacy Policy
          </Link>
          {" · "}
          <Link to="/cookies" className="underline underline-offset-2 text-foreground hover:text-primary transition-colors">
            Cookie Policy
          </Link>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm text-xs"
            onClick={reject}
          >
            Solo essenziali
          </Button>
          <Button
            size="sm"
            className="rounded-sm text-xs bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={accept}
          >
            Accetta tutti
          </Button>
          <button
            aria-label="Chiudi banner cookie"
            onClick={reject}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hook helper — restituisce true se l'utente ha accettato cookie analytics */
export function useCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    return JSON.parse(raw)?.accepted === true;
  } catch {
    return false;
  }
}
